import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "380",
  "slug": "what-is-the-international-date-line",
  "title": "What Is the International Date Line?",
  "excerpt": "Cross one invisible line in the Pacific and you can travel a whole day into the past or future. Here's what the International Date Line is and why it zigzags.",
  "date": "2026-06-08",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1553603227-2358aabe821e?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxwYWNpZmljJTIwb2NlYW4lMjBpc2xhbmR8ZW58MXwwfHx8MTc4MDkwNjI5MHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The International Date Line is an imaginary line running roughly down the 180° meridian in the Pacific Ocean. Cross it heading west and you skip forward a day; cross it heading east and you repeat a day. It zigzags to keep island nations on a single calendar date.",
  "content": [
    {
      "type": "paragraph",
      "text": "Time zones are confusing enough, but the International Date Line takes it further: it's the place where the calendar itself flips. Step across this invisible line in the middle of the Pacific and it can instantly become tomorrow — or yesterday. It sounds like science fiction, but it's a practical solution to a genuine problem."
    },
    {
      "type": "heading",
      "text": "Why We Need It at All"
    },
    {
      "type": "paragraph",
      "text": "The Earth is divided into time zones, each roughly an hour apart, as you move east or west. Travel all the way around the planet adding an hour at a time and you'd gain a full 24 hours — which makes no sense unless, at some point, you reset the date by a day. The International Date Line is that reset point."
    },
    {
      "type": "paragraph",
      "text": "Without it, two people circling the globe in opposite directions would arrive home disagreeing about what day it was. The line gives the world a single, agreed place to change the date."
    },
    {
      "type": "heading",
      "text": "Where It Runs"
    },
    {
      "type": "paragraph",
      "text": "The line roughly follows the 180° meridian — the line of longitude exactly opposite the Prime Meridian in Greenwich. That location is convenient: it falls mostly across the open Pacific Ocean, far from large populations, so the date change disrupts as few people as possible."
    },
    {
      "type": "paragraph",
      "text": "But it doesn't follow 180° exactly. Instead it bends and zigzags, and that's where it gets interesting."
    },
    {
      "type": "heading",
      "text": "Why It Zigzags"
    },
    {
      "type": "paragraph",
      "text": "If the line were perfectly straight, it would slice through the middle of some island nations, leaving one half of a country on Monday and the other on Tuesday. To avoid that chaos, the line detours around territories so each one stays on a single date."
    },
    {
      "type": "list",
      "items": [
        "It swings east around Kiribati, whose islands stretch across a huge span of the Pacific, so the whole nation shares one calendar day.",
        "It bends around Russia's far east and Alaska's Aleutian Islands to keep each country together.",
        "It curves near Samoa, Tonga, and other island groups according to the date each chooses to keep.",
        "Because it's a matter of convention rather than law, countries have occasionally shifted which side of the line they sit on."
      ]
    },
    {
      "type": "paragraph",
      "text": "Samoa famously jumped the line at the end of 2011, skipping December 30th entirely to align its calendar with major trading partners like Australia and New Zealand. Its people went to sleep on Thursday and woke up on Saturday."
    },
    {
      "type": "heading",
      "text": "Which Way Does the Date Change?"
    },
    {
      "type": "paragraph",
      "text": "Here's the simple rule. If you cross the line travelling west — say, flying from Hawaii toward Japan — you skip forward and lose a day. Cross it travelling east — from Japan back toward Hawaii — and you repeat a day, effectively gaining one. It's why a flight can land 'before' it took off in local calendar terms."
    },
    {
      "type": "heading",
      "text": "The First and Last Place on Earth"
    },
    {
      "type": "paragraph",
      "text": "Because of the line and its zigzags, some places are always among the first to greet a new day and others the last. Island nations near the line, such as Kiribati and Samoa, market themselves as the first to see each sunrise of the new year, while spots just on the other side are among the last."
    },
    {
      "type": "heading",
      "text": "The Islands a Day Apart"
    },
    {
      "type": "paragraph",
      "text": "The strangest place to witness the Date Line is the Bering Strait, where two small islands sit just a few kilometres apart. Big Diomede belongs to Russia and Little Diomede to the United States, and the Date Line runs between them. Despite being within sight of each other, they're nearly a full day apart on the calendar — which has earned them the nicknames 'Tomorrow Island' and 'Yesterday Isle'. On a clear day, you can look across the water at tomorrow."
    },
    {
      "type": "heading",
      "text": "A Reminder of How We Mapped the World"
    },
    {
      "type": "paragraph",
      "text": "The Date Line is a great example of how much of geography is human convention layered onto the physical Earth — time zones, borders, and meridians are all agreements we've drawn over the land and sea. Understanding those conventions makes the whole map easier to read."
    },
    {
      "type": "paragraph",
      "text": "Curious where the world's edges of time actually fall? Explore the Pacific in a round of EarthGuessr and see how scattered and remote those islands really are."
    }
  ]
};
